/**
 * Pay Rule Visual Component
 * Displays a graphical flowchart representation of the Pay Rule JSON structure
 * 
 * STYLING STANDARD (from Dashboard):
 * 
 * COMPLIANCE STATUS:
 * - Critical Non-Compliant: AlertTriangle, text-red-700, bg-red-100, border-red-700
 * - Non-Critical Non-Compliant (High): ShieldAlert, text-orange-600, bg-orange-50, border-orange-500
 * - Over-Compliant: ShieldPlus, text-amber-600, bg-amber-50, border-amber-500
 * - Compliant: ShieldCheck, text-emerald-600, bg-emerald-50, border-emerald-500
 * 
 * SEVERITY BADGES:
 * - Critical: bg-red-700 text-white
 * - High: bg-orange-500 text-white
 * - Medium: bg-amber-500 text-white
 * - Low: bg-sky-500 text-white
 * - Info: bg-slate-500 text-white
 */
import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Calendar, 
  Clock, 
  Settings, 
  AlertTriangle,
  Coffee,
  Timer,
  DollarSign,
  Star,
  PartyPopper,
  ShieldAlert,
  ShieldPlus,
  ShieldCheck
} from "lucide-react";

/**
 * Determines which sections have compliance issues based on issue categories/codes
 */
const getIssueMapping = (issues) => {
  const mapping = {
    pay_rule: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    fixed_rule: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    work_rule: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    overtime_rules: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    break_rules: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    zone_rules: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    holiday_credit_rule: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    pay_code_distribution: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false },
    holidays: { hasIssue: false, type: null, severity: null, count: 0, hasCritical: false }
  };

  if (!issues || !Array.isArray(issues)) return mapping;

  issues.forEach(issue => {
    const category = (issue.Category || '').toLowerCase();
    const code = (issue.Code || '').toLowerCase();
    const type = issue.Type;
    const severity = issue.Severity;
    const isCritical = severity === 'Critical';

    const updateMapping = (key) => {
      mapping[key].hasIssue = true;
      mapping[key].type = mapping[key].type || type;
      mapping[key].severity = isCritical ? 'Critical' : (mapping[key].severity || severity);
      mapping[key].count++;
      if (isCritical) mapping[key].hasCritical = true;
    };

    if (category.includes('overtime') || code.includes('ot') || code.includes('overtime')) {
      updateMapping('overtime_rules');
      updateMapping('work_rule');
    }

    if (category.includes('meal') || category.includes('rest') || category.includes('break') || 
        code.includes('meal') || code.includes('rest') || code.includes('break')) {
      updateMapping('break_rules');
      updateMapping('work_rule');
    }

    if (code.includes('zone') || code.includes('7th_day')) {
      updateMapping('zone_rules');
      updateMapping('work_rule');
    }

    if (category.includes('holiday') || code.includes('holiday')) {
      updateMapping('holiday_credit_rule');
    }

    if (category.includes('paycode') || code.includes('paycode') || code.includes('distribution')) {
      updateMapping('pay_code_distribution');
    }
  });

  return mapping;
};

/**
 * Get styling based on issue type and severity (Dashboard Standard)
 */
const getIssueStyles = (issueInfo) => {
  if (!issueInfo?.hasIssue) {
    return {
      borderColor: 'border-border',
      bgColor: 'bg-card',
      iconColor: 'text-muted-foreground',
      Icon: null
    };
  }

  if (issueInfo.type === 'Non-Compliant') {
    if (issueInfo.hasCritical) {
      // Critical Non-Compliant: AlertTriangle, red-700
      return {
        borderColor: 'border-red-700 border-2',
        bgColor: 'bg-red-100 dark:bg-red-950/50',
        iconColor: 'text-red-700',
        Icon: AlertTriangle
      };
    } else {
      // Non-Critical Non-Compliant: ShieldAlert, orange-600
      return {
        borderColor: 'border-orange-500 border-2',
        bgColor: 'bg-orange-50 dark:bg-orange-950/30',
        iconColor: 'text-orange-600',
        Icon: ShieldAlert
      };
    }
  }

  if (issueInfo.type === 'Over-Compliant') {
    // Over-Compliant: ShieldPlus, amber-600
    return {
      borderColor: 'border-amber-500 border-2',
      bgColor: 'bg-amber-50 dark:bg-amber-950/30',
      iconColor: 'text-amber-600',
      Icon: ShieldPlus
    };
  }

  return {
    borderColor: 'border-border',
    bgColor: 'bg-card',
    iconColor: 'text-muted-foreground',
    Icon: null
  };
};

/**
 * Compact Node component for the flowchart
 */
const FlowNode = ({ 
  label, 
  value, 
  icon: Icon, 
  issueInfo, 
  isRoot = false,
  isEmpty = false,
  compact = false
}) => {
  const styles = getIssueStyles(issueInfo);
  const BadgeIcon = styles.Icon;

  const nodeWidth = compact ? 'min-w-[110px] max-w-[120px]' : isRoot ? 'min-w-[180px]' : 'min-w-[140px] max-w-[150px]';
  const padding = compact ? 'px-2 py-2' : isRoot ? 'px-4 py-3' : 'px-3 py-2';

  const getIconColor = () => {
    if (issueInfo?.hasIssue) return styles.iconColor;
    return isRoot ? 'text-primary' : 'text-muted-foreground';
  };

  // Badge styling based on Dashboard standard
  const getBadgeStyle = () => {
    if (issueInfo?.type === 'Non-Compliant') {
      if (issueInfo.hasCritical) {
        return 'bg-red-700 text-white'; // Critical
      }
      return 'bg-orange-500 text-white'; // High
    }
    if (issueInfo?.type === 'Over-Compliant') {
      return 'bg-amber-500 text-white'; // Over-compliant
    }
    return 'bg-secondary text-secondary-foreground';
  };

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`
          relative rounded-lg border ${styles.borderColor} ${issueInfo?.hasIssue ? styles.bgColor : (isRoot ? 'bg-primary/10' : 'bg-card')}
          ${padding} ${nodeWidth}
          ${isEmpty ? 'opacity-50' : ''}
          shadow-sm transition-all hover:shadow-md
        `}
      >
        {/* Issue Badge */}
        {issueInfo?.hasIssue && issueInfo.count > 0 && (
          <div className="absolute -top-2 -right-2 z-10">
            <Badge className={`text-[9px] px-1 py-0 flex items-center ${getBadgeStyle()}`}>
              {BadgeIcon && <BadgeIcon className="w-2 h-2 mr-0.5" />}
              {issueInfo.count}
            </Badge>
          </div>
        )}

        {/* Content */}
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className={`w-3.5 h-3.5 ${getIconColor()} flex-shrink-0`} />}
          <div className="min-w-0">
            <p className={`text-[9px] uppercase tracking-wider ${isRoot ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
              {label}
            </p>
            <p className={`font-medium truncate ${isRoot ? 'text-xs' : 'text-[11px]'}`} title={value || ''}>
              {isEmpty ? '(Not set)' : value || 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PayRuleVisual = ({ open, onClose, payRuleJson, issues }) => {
  const parsedJson = useMemo(() => {
    if (!payRuleJson) return null;
    try {
      return typeof payRuleJson === 'string' ? JSON.parse(payRuleJson) : payRuleJson;
    } catch {
      return null;
    }
  }, [payRuleJson]);

  const issueMapping = useMemo(() => getIssueMapping(issues), [issues]);

  // Filter for actual compliance issues (Non-Compliant or Over-Compliant only)
  const complianceIssues = useMemo(() => {
    if (!issues || !Array.isArray(issues)) return [];
    return issues.filter(i => i.Type === 'Non-Compliant' || i.Type === 'Over-Compliant');
  }, [issues]);

  if (!parsedJson) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pay Rule Structure</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center h-40 text-muted-foreground">
            No pay rule JSON available
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    pay_rule_name,
    company,
    pay_rule,
    fixed_rule,
    work_rule,
    holiday_credit_rule,
    holidays
  } = parsedJson;

  const workRuleName = pay_rule?.effective_versions?.[0]?.['@WorkRuleName'];
  const fixedRuleName = pay_rule?.effective_versions?.[0]?.['@FixedRuleName'];
  const holidayCreditRuleName = pay_rule?.effective_versions?.[0]?.['@HolidayCreditRuleName'];

  const overtimeRules = work_rule?.overtime_rules || [];
  const breakRules = work_rule?.break_rules || [];
  const zoneRules = work_rule?.zone_rules || [];
  const payCodeDistribution = work_rule?.pay_code_distribution;

  const totalIssues = complianceIssues.length;
  const criticalCount = complianceIssues.filter(i => i.Severity === 'Critical').length;
  const hasNonCompliant = complianceIssues.some(i => i.Type === 'Non-Compliant');
  const hasCritical = criticalCount > 0;

  // Header badge styling based on Dashboard standard
  const getHeaderBadgeStyle = () => {
    if (hasNonCompliant) {
      if (hasCritical) {
        return 'border-red-700 text-red-700'; // Critical
      }
      return 'border-orange-500 text-orange-600'; // High/Non-critical
    }
    return 'border-amber-500 text-amber-600'; // Over-compliant only
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[900px] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-primary" />
            Pay Rule Structure Visualization
          </DialogTitle>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{company}</span>
            {totalIssues > 0 ? (
              <Badge variant="outline" className={`text-[10px] ${getHeaderBadgeStyle()}`}>
                {totalIssues} issue{totalIssues !== 1 ? 's' : ''} 
                {criticalCount > 0 && <span className="ml-1">({criticalCount} critical)</span>}
              </Badge>
            ) : (
              <Badge className="text-[10px] bg-emerald-500 text-white">
                <ShieldCheck className="w-3 h-3 mr-1" />
                All Compliant
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Legend - matches Dashboard */}
        <div className="flex items-center gap-3 px-3 py-1.5 bg-secondary/30 rounded-lg text-[10px] flex-shrink-0">
          <span className="text-muted-foreground">Legend:</span>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded border-2 border-red-700 bg-red-100" />
            <span>Critical</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded border-2 border-orange-500 bg-orange-50" />
            <span>Non-Compliant</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded border-2 border-amber-500 bg-amber-50" />
            <span>Over-Compliant</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded border border-border bg-card" />
            <span>Compliant</span>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-auto mt-3">
          <div className="flex flex-col items-center pb-6 min-w-fit px-4">
            
            {/* ROOT: Pay Rule */}
            <FlowNode
              label="Pay Rule"
              value={pay_rule_name}
              icon={FileText}
              issueInfo={issueMapping.pay_rule}
              isRoot={true}
            />

            {/* Connector */}
            <div className="w-px h-4 bg-border" />

            {/* LEVEL 2: Fixed Rule, Work Rule, Holiday Credit Rule */}
            <div className="flex items-start gap-4">
              {/* Fixed Rule */}
              <FlowNode
                label="Fixed Rule"
                value={fixed_rule?.name || fixedRuleName}
                icon={Calendar}
                issueInfo={issueMapping.fixed_rule}
                isEmpty={!fixed_rule?.name && !fixedRuleName}
              />

              {/* Work Rule - with children */}
              <div className="flex flex-col items-center">
                <FlowNode
                  label="Work Rule"
                  value={work_rule?.name || workRuleName}
                  icon={Settings}
                  issueInfo={issueMapping.work_rule}
                  isEmpty={!work_rule?.name && !workRuleName}
                />
                
                {/* Connector to sub-rules */}
                <div className="w-px h-4 bg-border" />
                
                {/* LEVEL 3: Work Rule Children - Compact grid */}
                <div className="flex items-start gap-2">
                  {/* Overtime Rules */}
                  <div className="flex flex-col items-center">
                    <div className="w-px h-3 bg-border" />
                    <FlowNode
                      label="OT Rules"
                      value={overtimeRules.length > 0 ? `${overtimeRules.length} rule${overtimeRules.length !== 1 ? 's' : ''}` : null}
                      icon={Clock}
                      issueInfo={issueMapping.overtime_rules}
                      isEmpty={overtimeRules.length === 0}
                      compact={true}
                    />
                  </div>

                  {/* Zone Rules */}
                  <div className="flex flex-col items-center">
                    <div className="w-px h-3 bg-border" />
                    <FlowNode
                      label="Zone Rules"
                      value={zoneRules.length > 0 ? `${zoneRules.length} rule${zoneRules.length !== 1 ? 's' : ''}` : null}
                      icon={Timer}
                      issueInfo={issueMapping.zone_rules}
                      isEmpty={zoneRules.length === 0}
                      compact={true}
                    />
                  </div>

                  {/* Break Rules */}
                  <div className="flex flex-col items-center">
                    <div className="w-px h-3 bg-border" />
                    <FlowNode
                      label="Break Rules"
                      value={breakRules.length > 0 ? `${breakRules.length} rule${breakRules.length !== 1 ? 's' : ''}` : null}
                      icon={Coffee}
                      issueInfo={issueMapping.break_rules}
                      isEmpty={breakRules.length === 0}
                      compact={true}
                    />
                  </div>

                  {/* Pay Code Distribution */}
                  <div className="flex flex-col items-center">
                    <div className="w-px h-3 bg-border" />
                    <FlowNode
                      label="Paycode"
                      value={payCodeDistribution ? 'Set' : null}
                      icon={DollarSign}
                      issueInfo={issueMapping.pay_code_distribution}
                      isEmpty={!payCodeDistribution}
                      compact={true}
                    />
                  </div>
                </div>
              </div>

              {/* Holiday Credit Rule */}
              <div className="flex flex-col items-center">
                <FlowNode
                  label="Holiday Credit"
                  value={holiday_credit_rule?.name || holidayCreditRuleName}
                  icon={Star}
                  issueInfo={issueMapping.holiday_credit_rule}
                  isEmpty={!holiday_credit_rule?.name && !holidayCreditRuleName}
                />
                
                {/* Holidays */}
                {holidays && holidays.length > 0 && (
                  <>
                    <div className="w-px h-3 bg-border" />
                    <FlowNode
                      label="Holidays"
                      value={`${holidays.length} set`}
                      icon={Calendar}
                      issueInfo={issueMapping.holidays}
                      compact={true}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Issue Details Section or All Compliant Message */}
            {totalIssues > 0 ? (
              <div className="mt-6 w-full max-w-xl">
                <div className="border rounded-lg p-3 bg-secondary/20">
                  <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                    {hasNonCompliant ? (
                      hasCritical ? (
                        <>
                          <AlertTriangle className="w-3.5 h-3.5 text-red-700" />
                          <span className="text-red-700">Critical Issues Detected</span>
                        </>
                      ) : (
                        <>
                          <ShieldAlert className="w-3.5 h-3.5 text-orange-600" />
                          <span className="text-orange-600">Compliance Issues Detected</span>
                        </>
                      )
                    ) : (
                      <>
                        <ShieldPlus className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-amber-600">Over-Compliance Detected</span>
                      </>
                    )}
                  </h4>
                  <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                    {complianceIssues.slice(0, 6).map((issue, idx) => {
                      // Determine styling based on Dashboard standard
                      const severity = issue.Severity?.toLowerCase();
                      const isNonCompliant = issue.Type === 'Non-Compliant';
                      
                      let cardBg, cardBorder, iconBg, IconComponent, iconColor, badgeBg, badgeText, categoryColor;
                      
                      // Get severity badge styling (matches Dashboard standard)
                      const getSeverityBadgeStyle = (sev) => {
                        switch (sev) {
                          case 'critical':
                            return { bg: 'bg-red-700', text: 'text-white', label: 'Critical' };
                          case 'high':
                            return { bg: 'bg-orange-500', text: 'text-white', label: 'High' };
                          case 'medium':
                            return { bg: 'bg-amber-500', text: 'text-white', label: 'Medium' };
                          case 'low':
                            return { bg: 'bg-sky-500', text: 'text-white', label: 'Low' };
                          case 'info':
                            return { bg: 'bg-slate-500', text: 'text-white', label: 'Info' };
                          default:
                            return { bg: 'bg-slate-500', text: 'text-white', label: 'Info' };
                        }
                      };
                      
                      const severityStyle = getSeverityBadgeStyle(severity);
                      
                      if (isNonCompliant) {
                        if (severity === 'critical') {
                          // Critical Non-Compliant
                          cardBg = 'bg-red-100 dark:bg-red-950/50';
                          cardBorder = 'border-red-700';
                          iconBg = 'bg-red-200 dark:bg-red-900/50';
                          IconComponent = AlertTriangle;
                          iconColor = 'text-red-700';
                          categoryColor = 'text-red-700 dark:text-red-400';
                        } else {
                          // Non-Critical Non-Compliant (High, Medium, etc.)
                          cardBg = 'bg-orange-50 dark:bg-orange-950/30';
                          cardBorder = 'border-orange-500';
                          iconBg = 'bg-orange-100 dark:bg-orange-900/50';
                          IconComponent = ShieldAlert;
                          iconColor = 'text-orange-600';
                          categoryColor = 'text-orange-600 dark:text-orange-400';
                        }
                        badgeBg = severityStyle.bg;
                        badgeText = severityStyle.text;
                      } else {
                        // Over-Compliant - always amber styling, Info badge
                        cardBg = 'bg-amber-50 dark:bg-amber-950/30';
                        cardBorder = 'border-amber-500';
                        iconBg = 'bg-amber-100 dark:bg-amber-900/50';
                        IconComponent = ShieldPlus;
                        iconColor = 'text-amber-600';
                        badgeBg = 'bg-amber-500';
                        badgeText = 'text-white';
                        categoryColor = 'text-amber-600 dark:text-amber-400';
                      }

                      return (
                        <div 
                          key={idx} 
                          className={`text-[11px] p-2 rounded flex items-start gap-2 ${cardBg} border ${cardBorder}`}
                        >
                          {/* Icon */}
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${iconBg}`}>
                            <IconComponent className={`w-3 h-3 ${iconColor}`} />
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Badge className={`text-[9px] px-1 py-0 ${badgeBg} ${badgeText}`}>
                                {isNonCompliant ? severityStyle.label : 'Info'}
                              </Badge>
                              <span className={`font-medium ${categoryColor}`}>
                                {issue.Category}
                              </span>
                            </div>
                            <p className="text-[10px] text-muted-foreground leading-relaxed">
                              {issue.Title || issue.Description || `${issue.Category} issue detected`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {totalIssues > 6 && (
                      <p className="text-[10px] text-muted-foreground text-center pt-1">
                        ... and {totalIssues - 6} more issue{totalIssues - 6 !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 w-full max-w-xl">
                <div className="border border-emerald-200 dark:border-emerald-900/50 rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <PartyPopper className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        Fully Compliant!
                      </h4>
                      <p className="text-xs text-emerald-600/80 dark:text-emerald-400/70">
                        This pay rule configuration meets all compliance requirements.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayRuleVisual;
